/**
 * The sea, and every other body of water, as filled shapes rather than lines.
 *
 * **The water was in the data all along, on two objects nobody drew.**
 * `docs/formats/gi-format.md` §6.1 recorded a pair of `scaleExponent = 10`
 * objects "larger than any region… not explained here", and the terrain lane's
 * census answered it (docs/engineering/lessons.md, 2026-08-23): `TERR1-00` is
 * **93 triangles of `BARON.PAL` index 4 over 378 × 245 km with a real
 * coastline**, and `TERR2-51` is the Flanders coast with the far English shore
 * drawn in as nine land polygons. They are the North Sea and the Channel.
 *
 * AD-016's hairline network deliberately excludes both — `map-network.ts`'s
 * `NETWORK_MAX_RADIUS_M` — and it was right to: *outlined*, a 615 km-radius plate
 * covers London's sheet in diagonal spokes reaching out to its own far
 * vertices. **Filled**, it is the single most useful mark on a coastal map.
 * So the same two objects come back through this path instead, as area rather
 * than as edges.
 *
 * The rule is uniform and needs no list of object names: **every triangle
 * whose `anchorIndex` is 4 is water** (`map-features.ts`). That takes in the
 * sea plates, London's Thames estuary (`TERR1-01`, 56 of its 136 faces),
 * Verdun's ponds and the rivers alike. A river fills to nothing — it is a
 * zero-width quad, 1.5 cm across (world-terrain.md §7.3) — and reaches the
 * sheet as its own boundary edge instead, which is exactly the line the
 * original's rasterizer draws.
 */

import { placementWorldTransform, type PlacementOrigin } from "../world/placement-transform.ts";
import type { WorldPlacementBody } from "../world/region-loader.ts";
import { mapPointOfWorld, type MapPoint } from "./map-model.ts";

/** One water triangle in map space — three points, wound as the model wound them. */
export interface MapWaterFace {
  readonly points: readonly [MapPoint, MapPoint, MapPoint];
}

/** A run of coastline: the edge of the water where it meets land or the plate's own limit. */
export interface MapWaterEdge {
  readonly a: MapPoint;
  readonly b: MapPoint;
}

export interface MapWater {
  readonly faces: readonly MapWaterFace[];
  readonly coastline: readonly MapWaterEdge[];
}

export const EMPTY_WATER: MapWater = { faces: [], coastline: [] };

/**
 * A placed object's model→map stamp: the instancer's `makeRotationY` written
 * out (`x' = cos·x + sin·z`, `z' = −sin·x + cos·z`) then dropped into map space
 * by `mapPointOfWorld`. Shared with `map-network.ts`, which stamps line
 * segments through the identical arithmetic — the two must agree vertex for
 * vertex or an object's coastline would sit beside its own river.
 */
export function placementStamp(placement: WorldPlacementBody, origin: PlacementOrigin): (x: number, z: number) => MapPoint {
  const { position, yawRadians } = placementWorldTransform(placement, origin);
  const cos = Math.cos(yawRadians);
  const sin = Math.sin(yawRadians);
  return (x, z) => mapPointOfWorld({ x: cos * x + sin * z + position.x, z: -sin * x + cos * z + position.z });
}

/**
 * How finely two endpoints must agree to count as the same corner, in metres.
 * A millimetre, the same tolerance `three/polygon-outlines.ts` uses to collapse
 * coincident edges — well below anything the terrain distinguishes and well
 * above float error in the placement transform.
 */
const COINCIDENT_TOLERANCE_M = 0.001;

function edgeKey(a: MapPoint, b: MapPoint): string {
  const quantise = (value: number) => Math.round(value / COINCIDENT_TOLERANCE_M);
  const first = `${quantise(a.u)},${quantise(a.v)}`;
  const second = `${quantise(b.u)},${quantise(b.v)}`;
  return first < second ? `${first}|${second}` : `${second}|${first}`;
}

/**
 * The outline of a set of water triangles: every edge that belongs to exactly
 * one of them.
 *
 * This is what turns a fan-triangulated plate into a coastline. An interior
 * edge — the diagonal that splits a quad, the spoke of a fan — is shared by
 * the two faces either side of it and cancels; the edge where the water stops
 * is not, and survives. That is precisely the difference between the AD-016
 * outline (every edge of every triangle, spokes and all) and a map of the
 * shore, and it is why the plate that was unusable as a network object is
 * usable here.
 */
export function coastlineOf(faces: readonly MapWaterFace[]): MapWaterEdge[] {
  const counts = new Map<string, number>();
  const edges: MapWaterEdge[] = [];
  for (const face of faces) {
    for (let corner = 0; corner < 3; corner += 1) {
      const a = face.points[corner]!;
      const b = face.points[(corner + 1) % 3]!;
      const key = edgeKey(a, b);
      counts.set(key, (counts.get(key) ?? 0) + 1);
      edges.push({ a, b });
    }
  }
  return edges.filter((edge) => counts.get(edgeKey(edge.a, edge.b)) === 1);
}

/**
 * Stamps one object's water triangles down at each of its placements. The
 * positions arrive in the model's world-oriented frame — the caller has already
 * applied `modelFrameToWorld`, the same −90° X fix the 3D instancer applies
 * with `geometry.rotateX` — so all that is left is the yaw and the offset.
 */
export function stampWaterFaces(
  positions: Float32Array,
  waterTriangles: readonly number[],
  placements: readonly WorldPlacementBody[],
  origin: PlacementOrigin,
  into: MapWaterFace[],
): void {
  for (const placement of placements) {
    const stamp = placementStamp(placement, origin);
    for (const triangle of waterTriangles) {
      const base = triangle * 9;
      const corners = [0, 3, 6].map((offset) => stamp(positions[base + offset] ?? 0, positions[base + offset + 2] ?? 0));
      const [a, b, c] = corners;
      if (a && b && c) into.push({ points: [a, b, c] });
    }
  }
}
